import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { ClientsApiService } from 'src/app/core/clients-api/clients-api.service';
import { EmployeesApiService } from 'src/app/core/employees-api/employees-api.service';
import { ComponentCanDeactivate } from 'src/app/core/guards/can-deactivate.guard';
import { MandatesApiService } from 'src/app/core/mandates-api/mandates-api.service';
import MandateListItem from 'src/app/service/dto/MandateListItem';
import { LanguageService } from 'src/app/service/language.service';
import EngagementType from 'src/app/service/model/EngagementType';
import { ToastService } from 'src/app/service/toast.service';
import { BaseDialog } from 'src/app/shared/components/base-dialog/base-dialog';
import { AuthService } from 'src/app/core/authentication/auth.service';
import Client from 'src/app/service/model/Client';
import EmploymentRoleType from 'src/app/service/model/EmploymentRoleType';
import MandateStatus from 'src/app/service/model/MandateStatus';
import MoralPerson from 'src/app/service/model/MoralPerson';
import NaturalPerson from 'src/app/service/model/NaturalPerson';
import PersonType from 'src/app/service/model/PersonType';

import { TerminateClientDialog } from './terminate-client-dialog/terminate-client-dialog';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'mandates-client-detail',
  templateUrl: './client-detail.component.html',
  styleUrls: ['./client-detail.component.scss'],
})
export class ClientDetailComponent implements OnInit, ComponentCanDeactivate, OnDestroy {
  id: string;
  client: Client;
  index = 0;
  isLoading: boolean = true;
  isEditing: boolean = false;
  isExclusive: boolean;

  mandateTableColumns = ['mandateTypes', 'lastOpened', 'status', 'actions'];
  status = [
    MandateStatus.TO_SEND,
    MandateStatus.SENDING,
    MandateStatus.SENT,
    MandateStatus.AWAITING_APPROVAL,
    MandateStatus.ACTIVE,
    MandateStatus.ERROR,
    MandateStatus.DECLINED,
    MandateStatus.TERMINATED,
  ];

  mandates: MandateListItem[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private clientApiService: ClientsApiService,
    private mandatesApiService: MandatesApiService,
    private employeesApiService: EmployeesApiService,
    public translateService: TranslateService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private toast: ToastService,
    private languageService: LanguageService,
    private matDialog: MatDialog,
    private authService: AuthService
  ) {}

  canDeactivate() {
    return {
      shouldDeactivate: !this.isEditing,
      data: {
        title: 'DIALOG.CANCEL-FORM.DESCRIPTION-PERSON-UPDATE',
        description: '',
        cancelText: 'DIALOG.NO-CONTINUE',
        confirmText: 'DIALOG.YES-CANCEL',
        confirmColor: 'accent',
      },
    };
  }

  ngOnInit(): void {
    this.employeesApiService.getEmployees().subscribe();
    this.isEditing = false;
    this.fetchClient();

    this.clientApiService.isExclusive$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isExclusive) => (this.isExclusive = isExclusive));

    this.mandatesApiService.mandates$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => (this.mandates = result?.content || []));

    this.clientApiService.selectedClient$
      .pipe(takeUntil(this.destroy$))
      .subscribe((client) => {
        if (client) {
          this.client = client;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  returnPreviousPage() {
    this.location.back();
  }

  terminateClient() {
    this.matDialog
      .open(TerminateClientDialog, {
        data: {
          client: this.client,
          mandates: this.mandates || [],
        },
      })
      .afterClosed()
      .subscribe((response) => {
        if (response) {
          this.clientApiService.terminateClientById(this.id).subscribe(() => this.location.back());
        }
      });
  }

  private fetchClient(): void {
    this.route.params.pipe(
      switchMap(params => {
        const id = params['clientId'];
        if (!id) return throwError(() => new Error('Could not get Client Id'));

        this.id = id;
        this.isLoading = true;
        return this.clientApiService.getClientById(this.id);
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (response) => {
        this.client = response;
        this.clientApiService.selectedClient$.next(response);
        this.isLoading = false;
      },
      error: (cause) => {
        const errorMessage = cause instanceof HttpErrorResponse ? `[${cause.status}: ${cause.name}] ${cause.message}` : cause.message;
        this.toast.openErrorToast(errorMessage);
        this.isLoading = false;
      },
    });
  }

  editClient() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
      this.router.navigate([currentUrl], {
        queryParams: { editClient: true },
      })
    );
  }

  get clientType(): PersonType {
    return this.client.person.type;
  }

  get clientName(): string {
    return this.client.person.getScreenName();
  }

  get nationalNumber(): string {
    if ((this.client.person as NaturalPerson).nationalNumber) {
      return (this.client.person as NaturalPerson).nationalNumber;
    }
    return '-';
  }

  get companyNumber(): string {
    if ((this.client.person as MoralPerson).companyNumber) {
      return (this.client.person as MoralPerson).companyNumber;
    }
    return '-';
  }

  get address() {
    const { street, streetNumber, box, zipCode, city, country } = this.client.person.address || {};
    return street && streetNumber && zipCode && city && country
      ? `${street} ${streetNumber} ${
          box ? box : ''
        }, ${zipCode} ${city} (${this.languageService.getCountryName(country)})`
      : '-';
  }

  get clientInfo(): { name: string; value: any }[] {
    return [
      { name: 'COMMON.COMPANY-NUMBER', value: this.companyNumber },
      { name: 'COMMON.NATIONAL-NUMBER', value: this.nationalNumber },
      { name: 'COMMON.ADDRESS', value: this.address },
    ];
  }

  get selectedClient$(): Observable<Client | undefined> {
    return this.clientApiService.selectedClient$;
  }

  get isComplete(): boolean {
    const { street, streetNumber, zipCode, city, country } = this.client.person.address || {};
    return !!country && !!city && !!zipCode && !!street && !!streetNumber;
  }

  onAccessClick() {
    const type = !this.isExclusive ? EngagementType.EXCLUSIVE : EngagementType.COMPLEMENTARY;
    if (
      (type === EngagementType.COMPLEMENTARY &&
        this.authService.userInfo?.role === EmploymentRoleType.ADMIN) ||
      type === EngagementType.EXCLUSIVE
    ) {
      this.openDialog(type);
    }
  }

  private openDialog(type: EngagementType) {
    const title =
      type == EngagementType.EXCLUSIVE
        ? 'DIALOG.UPDATE-CLIENT-ACCESS.LIMITED.DESCRIPTION'
        : 'DIALOG.UPDATE-CLIENT-ACCESS.PUBLIC.DESCRIPTION';
    const paragraph =
      type == EngagementType.EXCLUSIVE
        ? 'DIALOG.UPDATE-CLIENT-ACCESS.LIMITED.PARAGRAPH'
        : 'DIALOG.UPDATE-CLIENT-ACCESS.PUBLIC.PARAGRAPH';
    const dialogRef = this.matDialog.open(BaseDialog, {
      data: {
        title: this.translateService.instant(title, {
          name: this.client.person.getScreenName(),
        }),
        description: paragraph,
        cancelText: 'DIALOG.NO',
        confirmText: 'DIALOG.YES',
        confirmColor: 'accent',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isExclusive = !this.isExclusive;
        this.clientApiService.updateEngagementType(this.id, type).subscribe();
      }
    });
  }
}
